/**
 * EntityDetailModal — generic detail-modal frame.
 *
 * Provides the reusable surface (backdrop, focus management, escape/click-out
 * dismissal, body-scroll lock) and three slot regions:
 *
 *   - header              — top region; rendered above the divider
 *   - body                — scrollable middle; the main content
 *   - footerSelectionArea — bottom-left; SelectionToggle + secondary actions
 *   - footerPrimaryAction — bottom-right; the page CTA ("View company page")
 *
 * Entity-specific composition (CompanyDetailModal, PositionDetailModal, etc.)
 * builds the inner content and slots it in here. The frame itself knows
 * nothing about what's inside.
 *
 * Props:
 *   open                 — bool
 *   onClose              — () => void
 *   header               — ReactNode (typically a header row with logo/title)
 *   body                 — ReactNode
 *   footerSelectionArea  — ReactNode; left-justified bottom slot
 *   footerPrimaryAction  — ReactNode; right-justified bottom slot
 *   maxWidth             — MUI Dialog maxWidth (default "md")
 */
import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const EntityDetailModal = ({
  open,
  onClose,
  header,
  body,
  footerSelectionArea,
  footerPrimaryAction,
  maxWidth = 'md',
}) => {
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      // MUI Dialog handles focus trap, escape dismissal, scroll lock,
      // and aria attributes. We only have to provide content.
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {header && (
          <>
            <Box sx={{ p: 3, pr: 6 }}>{header}</Box>
            <Divider />
          </>
        )}
      </Box>

      <DialogContent sx={{ p: 3 }}>{body}</DialogContent>

      {(footerSelectionArea || footerPrimaryAction) && (
        <>
          <Divider />
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              px: 3,
              py: 2,
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {footerSelectionArea}
            </Box>
            <Box>{footerPrimaryAction}</Box>
          </Stack>
        </>
      )}
    </Dialog>
  );
};

export default EntityDetailModal;
